// src/components/SquadTrackerPage.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import Header from './Header';
import '../css/SquadTrackerPage.css';
import TalentProfileModal from './TalentProfileModal';
import GamifiedConfirmModal from './GamifiedConfirmModal';
import { useLocation } from 'react-router-dom';

import { useCredits } from './CreditsContext';
import { getShortlist, addToShortlist, removeFromShortlist, updateShortlistStage, updateShortlistStatus } from '../data/shortlist';
import { getUserTalents, saveUserTalent, deleteUserTalent, moveFromShortlistToUserTalents } from '../data/userTalents';
import { useAuth } from '../contexts/AuthContext';

/* =========================
   Constants
========================= */
const STAGES = ['Shortlist', 'Pre-Onboarding', 'Onboarding', 'Deployment', 'Offboarding'];

const LIFECYCLE_STATUSES = {
  Shortlist: [],
  'Pre-Onboarding': [
    'Talent Sourcing & Pooling',
    'Screening & Assessments',
    'Structured Interviews',
    'Client Interviews',
  ],
  Onboarding: [
    'Background Checks & Verification',
    'Medical Screening',
    'Employment Documents',
    'Work Enablement / Workplace Setup',
  ],
  Deployment: ['Training', 'Nesting', 'Go-Live'],
  Offboarding: ['Pre-Offboarding', 'Offboarding', 'Post-Offboarding'],
};

const DEFAULT_STATUS = {
  'Pre-Onboarding': 'Talent Sourcing & Pooling',
  Onboarding: 'Background Checks & Verification',
  Deployment: 'Training',
  Offboarding: 'Pre-Offboarding',
};

// Map current -> next stage
const NEXT_STAGE = {
  Shortlist: 'Pre-Onboarding',
  'Pre-Onboarding': 'Onboarding',
  Onboarding: 'Deployment',
  Deployment: 'Offboarding',
};

/* =========================
   Page
========================= */
export default function SquadTrackerPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('all'); // <-- main filter the page uses
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('stage');
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeStatStage, setActiveStatStage] = useState(null);

  const { currentUser } = useAuth();
  const { refundScreening, consumeScreening } = useCredits();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTalent, setModalTalent] = useState(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRow, setCancelRow] = useState(null);

  // Removed rowsRaw, loading, err, debounce as we only use shortlist data now

  const [busyId, setBusyId] = useState(null);

  // User verification state
  const [userData, setUserData] = useState({
    isVerified: false,
    isLoading: true
  });

  // Verification modal state
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const location = useLocation();

  // Fetch user verification status
  const fetchUserData = async () => {
    if (!currentUser?.uid) {
      setUserData({ isVerified: false, isLoading: false });
      return;
    }

    try {
      setUserData(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
      
      if (response.ok) {
        const result = await response.json();
        const user = result.data;
        
        setUserData({
          isVerified: user?.is_verified || user?.email_verified || false,
          isLoading: false
        });
      } else {
        setUserData({ isVerified: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData({ isVerified: false, isLoading: false });
    }
  };

  useEffect(() => {
    if (currentUser?.uid) {
      fetchUserData();
    }
  }, [currentUser?.uid]);

  // Deep-link helpers
  const [highlightTalentId, setHighlightTalentId] = useState(null);
  const [deepLinkedStage, setDeepLinkedStage] = useState(null);
  const firstLoadRef = useRef(true);

  // Helpers
  const normalizeStage = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (/^\d+$/.test(s)) {
      const idx = Math.max(0, Math.min(STAGES.length - 1, Number(s)));
      return STAGES[idx];
    }
    const exact = STAGES.find(x => x === s);
    if (exact) return exact;
    const loose = STAGES.find(x => x.toLowerCase() === s.toLowerCase());
    return loose || null;
  };

  // Read initial stage/talent from either location.state or ?stage=&talentId=
  useEffect(() => {
    if (!firstLoadRef.current) return;
    firstLoadRef.current = false;

    const params = new URLSearchParams(location.search || '');
    const qsStage = params.get('stage');
    const qsTalent = params.get('talentId');
    const stg = normalizeStage(location.state?.stage || qsStage);
    const tid = location.state?.highlightTalentId || qsTalent;

    if (stg && STAGES.includes(stg)) {
      setSelectedStage(stg);       // 🔎 apply filter immediately (overview table + status dropdown)
      setDeepLinkedStage(stg);     // 👻 hide other stages in stats & "By Stage" sections
      setActiveStatStage(null);    // ensure stats aren’t expanded unless user clicks
      setSelectedStatus('all');
    }
    if (tid) setHighlightTalentId(String(tid));
  }, [location]);

  // highlight target row after the data + DOM settle
  useEffect(() => {
    if (!highlightTalentId) return;
    const id = setTimeout(() => {
      const row = document.querySelector(`[data-talent-id="${CSS.escape(highlightTalentId)}"]`);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row.classList.add('pulse-highlight');
        setTimeout(() => row.classList.remove('pulse-highlight'), 1400);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [highlightTalentId, selectedStage, selectedStatus]);

  // 🔄 reactive shortlist (API-backed for current user)
  const [shortlist, setShortlist] = useState([]);
  useEffect(() => {
    const loadShortlist = async () => {
      if (currentUser?.uid) {
        try {
          const userShortlist = await getShortlist(currentUser.uid);
          setShortlist(userShortlist);
        } catch (error) {
          console.error('Failed to load shortlist:', error);
          setShortlist([]);
        }
      } else {
        setShortlist([]);
      }
    };
    
    loadShortlist();
    
    const refresh = () => loadShortlist();
    window.addEventListener('exodia:shortlist-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('exodia:shortlist-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [currentUser?.uid]);

  // Helpers
  const mkEmail = (name = '') =>
    `${(name || '')
      .toLowerCase()
      .replace(/[^a-z]+/g, '.')
      .replace(/\.+/g, '.')
      .replace(/^\.|\.$/g, '') || 'talent'}@exodia.com`;

  const inferDepartmentFromRole = (role = '') => {
    const r = role.toLowerCase();
    if (/engineer|developer|devops|data/.test(r)) return 'Engineering';
    if (/designer|artist|animator/.test(r)) return 'Design & Art';
    if (/product/.test(r)) return 'Product';
    if (/support|customer/.test(r)) return 'Operations';
    return 'General';
  };

  const statusFromAvailability = (availability = '') => {
    const a = (availability || '').toLowerCase();
    if (a.includes('immediate')) return 'Scheduled';
    if (a.includes('week')) return 'In Progress';
    if (a.includes('month')) return 'Pending';
    return 'Pending';
  };

  // Removed load function as we only use shortlist data now

  // Removed useEffect calls for loading database data as we only use shortlist data now

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Convert shortlist -> employee rows (all stages)
  const shortlistEmployees = useMemo(() => {
    return (shortlist || []).map(s => ({
      id: s.talent_id ?? s.talentId ?? s.id,
      name: s.talent_name ?? s.name,
      role: s.talent_role ?? s.role,
      alias: s.talent_name ?? s.name,
      stage: s.stage || 'Shortlist', // Use actual stage from database
      status: s.status || null, // Use actual status from database
      seniority: s.seniority || 'Mid',
      offerStatus: '—',
      startDate: 'TBD',
      email: mkEmail(s.talent_name ?? s.name),
      phone: '—',
      location: '—',
      department: inferDepartmentFromRole(s.talent_role ?? s.role),
      manager: '—',
      salary: '—',
      documents: [],
      notes: '',
      __raw: s,
    }));
  }, [shortlist]);

  // Use only shortlisted talents
  const employees = useMemo(() => {
    return shortlistEmployees;
  }, [shortlistEmployees]);

  // Group by stage (for “By Stage” sections)
  // const groupedByStage = useMemo(() => {
  //   const groups = Object.fromEntries(STAGES.map(s => [s, []]));
  //   for (const e of employees) if (groups[e.stage]) groups[e.stage].push(e);
  //   for (const s of STAGES) groups[s].sort((a, b) => (a.alias || a.name || '').localeCompare(b.alias || b.name || ''));
  //   return groups;
  // }, [employees]);

  // Stats
  const onClickStatCard = (stage) => {
    setActiveStatStage(prev => (prev === stage ? null : stage));
    setSelectedStage(prev => (prev === stage ? 'all' : stage)); // keep filter in sync
    setSelectedStatus('all');
    setDeepLinkedStage(null); // user interacted; show full UI again
  };

  const stageTotals = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map(s => [s, 0]));
    for (const e of employees) if (STAGES.includes(e.stage)) counts[e.stage] += 1;
    return counts;
  }, [employees]);

  const getSubstatusCounts = (stage) => {
    const items = LIFECYCLE_STATUSES[stage] || [];
    return items.map((name) => ({
      name,
      count: employees.filter((e) => e.stage === stage && e.status === name).length,
    }));
  };

  // Filter + sort for the main overview table
  const filteredAndSortedEmployees = useMemo(() => {
    let list = employees.filter((employee) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        (employee.name || '').toLowerCase().includes(q) ||
        (employee.role || '').toLowerCase().includes(q) ||
        (employee.email || '').toLowerCase().includes(q);
      const matchesStage = selectedStage === 'all' || employee.stage === selectedStage;
      const matchesStatus = selectedStatus === 'all' || employee.status === selectedStatus;
      return matchesSearch && matchesStage && matchesStatus;
    });

    list.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (sortBy === 'startDate') { aValue = new Date(a.startDate); bValue = new Date(b.startDate); }
      if (aValue === bValue) return 0;
      return sortOrder === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });

    return list;
  }, [employees, searchTerm, selectedStage, selectedStatus, sortBy, sortOrder]);

  const getStageColor = (stage) =>
    ({
      Shortlist: '#a78bfa',
      'Pre-Onboarding': '#ffbb33',
      Onboarding: '#00ff88',
      Deployment: '#4dd0e1',
      Offboarding: '#ff6b6b',
    }[stage] || '#cccccc');

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const openTalentModal = (talent) => { setModalTalent(talent); setModalOpen(true); };
  const closeTalentModal = () => { setModalOpen(false); setModalTalent(null); };

  /* =========================
     Start Pre-Onboarding from Shortlist
  ========================= */
  const startPreOnboardingFromShortlist = async (row) => {
    // Check verification before proceeding
    if (userData.isLoading) {
      setShowVerificationPrompt(true);
      return;
    }

    if (!userData.isVerified) {
      setShowVerificationPrompt(true);
      return;
    }

    const raw = row?.__raw || row || {};
    const id = raw.talent_id ?? raw.talentId ?? raw.id ?? row.id;
    
    // Consume 1 screening credit for starting pre-onboarding
    const creditOk = consumeScreening(1);
    if (!creditOk) {
      alert('Insufficient screening credits. You need 1 screening credit to start pre-onboarding.');
      return;
    }
    
    try {
      setBusyId(id);

      // Simply update stage to Pre-Onboarding
      if (currentUser?.uid) {
        await updateShortlistStage(id, currentUser.uid, 'Pre-Onboarding');
        // Reload shortlist to show updated stage
        const updatedShortlist = await getShortlist(currentUser.uid);
        setShortlist(updatedShortlist);
      }

    } catch (e) {
      setErrorMsg(e?.message || String(e));
      // Rollback: refund the screening credit if stage update failed
      refundScreening(1);
    } finally {
      setBusyId(null);
    }
  };

  /* =========================
     Compute next step
  ========================= */
  const getNextStep = (row) => {
    const stage = row.stage;

    if (stage === 'Shortlist') {
      return { label: 'Start Pre-Onboarding', type: 'start-pre' };
    }

    const statuses = LIFECYCLE_STATUSES[stage] || [];
    const curr = row.status || null;
    const idx = statuses.findIndex(s => s === curr);

    if (statuses.length > 0) {
      if (idx === -1) {
        const first = statuses[0];
        return { label: `Start: ${first}`, type: 'advance', patch: { status: first } };
      }
      if (idx < statuses.length - 1) {
        const next = statuses[idx + 1];
        return { label: `Next: ${next}`, type: 'advance', patch: { status: next } };
      }

      const nextStage = NEXT_STAGE[stage];
      if (!nextStage) {
        return { label: 'Finish Offboarding', type: 'delete' };
      }
      const nextFirst = LIFECYCLE_STATUSES[nextStage]?.[0] || null;
      const patch = nextFirst ? { stage: nextStage, status: nextFirst } : { stage: nextStage, status: null };
      return { label: `Go to ${nextStage}`, type: 'advance', patch };
    }

    const nextStage = NEXT_STAGE[stage];
    if (!nextStage) return { label: 'Completed', type: 'noop' };
    const nextFirst = LIFECYCLE_STATUSES[nextStage]?.[0] || null;
    const patch = nextFirst ? { stage: nextStage, status: nextFirst } : { stage: nextStage, status: null };
    return { label: `Go to ${nextStage}`, type: 'advance', patch };
  };

  /* =========================
     Perform the next step
  ========================= */
  const handleAdvance = async (row) => {
    const action = getNextStep(row);

    if (action.type === 'start-pre') {
      return startPreOnboardingFromShortlist(row);
    }

    const rowid = row.__raw?.ROWID;
    if (!rowid && action.type !== 'noop') {
      alert('Please save this talent first before advancing.');
      return;
    }

    try {
      setBusyId(row.__raw?.talentId ?? row.id);

      if (action.type === 'advance' && action.patch) {
        // Update stage and/or status in the shortlist
        if (currentUser?.uid) {
          const id = row.__raw?.talent_id ?? row.__raw?.talentId ?? row.id;
          
          if (action.patch.stage && action.patch.status) {
            // Both stage and status are changing
            await updateShortlistStage(id, currentUser.uid, action.patch.stage, action.patch.status);
          } else if (action.patch.stage) {
            // Only stage is changing
            await updateShortlistStage(id, currentUser.uid, action.patch.stage);
          } else if (action.patch.status) {
            // Only status is changing
            await updateShortlistStatus(id, currentUser.uid, action.patch.status);
          }
          
          // Reload shortlist to show updated data
          const updatedShortlist = await getShortlist(currentUser.uid);
          setShortlist(updatedShortlist);
        }
      } else if (action.type === 'delete') {
        // Handle completion/removal
        if (currentUser?.uid) {
          const id = row.__raw?.talent_id ?? row.__raw?.talentId ?? row.id;
          await removeFromShortlist(id, currentUser.uid);
          // Reload shortlist
          const updatedShortlist = await getShortlist(currentUser.uid);
          setShortlist(updatedShortlist);
        }
      }
    } catch (e) {
      setErrorMsg(e?.message || String(e));
    } finally {
      setBusyId(null);
    }
  };

  /* =========================
     Cancel from Shortlist
  ========================= */
  const handleCancelShortlist = async (row) => {
    const raw = row?.__raw || row || {};
    const id = raw.talent_id ?? raw.talentId ?? raw.id ?? row.id;

    try {
      setBusyId(id);
      if (currentUser?.uid) {
        await removeFromShortlist(id, currentUser.uid);
        // Reload shortlist to show updated data
        const updatedShortlist = await getShortlist(currentUser.uid);
        setShortlist(updatedShortlist);
      }
      // Credit refund removed - no longer refunding credits for canceling shortlist
    } catch (e) {
      if (currentUser?.uid) {
        await addToShortlist(raw, currentUser.uid);
      }
      setErrorMsg(e?.message || String(e));
    } finally {
      setBusyId(null);
    }
  };

  /* =========================
     UI helpers
  ========================= */
  const ActionButtons = ({ employee }) => {
    const rowId = employee.__raw?.talentId ?? employee.id;
    const isBusy = busyId === rowId;
    const next = getNextStep(employee);

    return (
      <>
        <button
          className="action-btn hire"
          onClick={() => handleAdvance(employee)}
          disabled={isBusy || next.type === 'noop'}
          title={next.label}
        >
          {isBusy ? 'Working…' : next.label}
        </button>

        {employee.stage === 'Shortlist' ? (
          <button
            className="action-btn hire"
            onClick={() => { setCancelRow(employee); setCancelOpen(true); }}
            disabled={isBusy}
            title="Remove from Shortlist"
          >
            {isBusy ? 'Cancelling…' : 'Cancel'}
          </button>
        ) : (
          <button className="action-btn view" onClick={() => openTalentModal(employee)}>
            View
          </button>
        )}
      </>
    );
  };

  // const renderStageSection = (stage) => {
  //   const items = groupedByStage[stage] || [];
  //   const showStatus = stage !== 'Shortlist';

  //   return (
  //     <div key={stage} className="stage-section">
  //       <div className="table-header" style={{ marginTop: 24 }}>
  //         <h3 className="section-title">{stage}</h3>
  //         <span className="results-count">{items.length} talent{items.length !== 1 ? 's' : ''}</span>
  //       </div>

  //       <div className="table-container">
  //         <table className="employees-table">
  //           <thead>
  //             <tr>
  //               <th>ID</th>
  //               <th>Name</th>
  //               <th>Role</th>
  //               {showStatus && <th>Status</th>}
  //               <th>Actions</th>
  //             </tr>
  //           </thead>
  //           <tbody>
  //             {items.length ? items.map((employee) => (
  //               <tr
  //                 key={`${stage}-${employee.id}`}
  //                 className="employee-row"
  //                 data-talent-id={employee.__raw?.talentId ?? employee.id ?? employee.__raw?.ROWID}
  //               >
  //                 <td>{employee.id}</td>
  //                 <td>{employee.alias || employee.name}</td>
  //                 <td>{employee.role}</td>
  //                 {showStatus && (
  //                   <td>
  //                     <span className="stage-badge" style={{ backgroundColor: getStageColor(employee.stage) }}>
  //                       {employee.status ?? '—'}
  //                     </span>
  //                   </td>
  //                 )}
  //                 <td className="employee-actions">
  //                   <div className="action-buttons">
  //                     <ActionButtons employee={employee} />
  //                   </div>
  //                 </td>
  //               </tr>
  //             )) : (
  //               <tr>
  //                 <td colSpan={showStatus ? 5 : 4} style={{ textAlign: 'center', padding: '1rem', color: '#888' }}>
  //                   No talents in {stage}.
  //                 </td>
  //               </tr>
  //             )}
  //           </tbody>
  //         </table>
  //       </div>
  //     </div>
  //   );
  // };

  // Stages to show in cards/sections: if deep-linked, only that stage
  const stagesToShow = deepLinkedStage ? [deepLinkedStage] : STAGES;

  return (
    <div className="squad-tracker-page">
      <style>
        {`
          @keyframes fadeIn {
            from { 
              opacity: 0;
            }
            to { 
              opacity: 1;
            }
          }
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.9);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-10px);
            }
            60% {
              transform: translateY(-5px);
            }
          }
        `}
      </style>
      <Header />

      <main className="squad-main">
        {/* Hero */}
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">Squad Tracker</h1>
            <p className="hero-subtitle">Step-by-step lifecycle with sub-stages</p>
            <p className="hero-description">
              Click the primary action to advance a talent through each sub-step. The final Offboarding step removes the talent.
            </p>
          </div>
          <div className="hero-visual">
            <div className="lifecycle-flow">
              <div className="flow-step"><span className="flow-icon">⭐</span><span className="flow-label">Shortlist</span></div>
              <div className="flow-arrow">→</div>
              <div className="flow-step"><span className="flow-icon">🔍</span><span className="flow-label">Pre-Onboarding</span></div>
              <div className="flow-arrow">→</div>
              <div className="flow-step"><span className="flow-icon">🚀</span><span className="flow-label">Onboarding</span></div>
              <div className="flow-arrow">→</div>
              <div className="flow-step"><span className="flow-icon">🛰️</span><span className="flow-label">Deployment</span></div>
              <div className="flow-arrow">→</div>
              <div className="flow-step"><span className="flow-icon">👋</span><span className="flow-label">Offboarding</span></div>
            </div>
          </div>
        </section>

        {/* Lifecycle “buttons” (stats cards) */}
        <section className="stats-section">
          <div className="stats-grid">
            {stagesToShow.map((stage) => (
              <div
                key={stage}
                className={`stat-card ${activeStatStage === stage ? 'expanded' : ''}`}
                onClick={() => onClickStatCard(stage)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClickStatCard(stage)}
              >
                {activeStatStage === stage ? (
                  <div className="stat-expand">
                    <div className="expand-title">{stage}</div>
                    <div className="expand-divider" />
                    <div className="expand-list">
                      {getSubstatusCounts(stage).map((item) => (
                        <div key={item.name} className="expand-row">
                          <span className="expand-name">{item.name}</span>
                          <span className="expand-dash">-</span>
                          <span className="expand-count">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="stat-content">
                    <span className="stat-label">{stage}</span>
                    <span className="stat-number">{stageTotals[stage] || 0}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Filters */}
        <section className="filters-section">
          <div className="filters-container">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search by name, role, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-controls">
              <select
                value={selectedStage}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedStage(v);
                  setSelectedStatus('all');
                  setDeepLinkedStage(null); // user changed filter; show all stages UI again
                }}
                className="filter-select"
              >
                <option value="all">All Stages</option>
                {STAGES.map((stage) => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="filter-select"
                disabled={selectedStage === 'all'}
              >
                <option value="all">All Statuses</option>
                {(selectedStage !== 'all' ? LIFECYCLE_STATUSES[selectedStage] : []).map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Overview table */}
        <section className="table-section">
          <div className="table-header">
            <h2 className="section-title">Employee Lifecycle</h2>
            <span className="results-count">
              {err ? `Error: ${err}` : loading ? 'Loading…' : errorMsg ? `Error: ${errorMsg}` : `${filteredAndSortedEmployees.length} employees found`}
            </span>
          </div>

          <div className="table-container">
            <table className="employees-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID {sortBy === 'id' && (<span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Name {sortBy === 'name' && (<span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                  <th onClick={() => handleSort('role')} className="sortable">
                    Role {sortBy === 'role' && (<span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                  <th onClick={() => handleSort('stage')} className="sortable">
                    Stage {sortBy === 'stage' && (<span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    Status {sortBy === 'status' && (<span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                  <th onClick={() => handleSort('actions')} className="sortable">
                    Actions {sortBy === 'actions' && (<span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredAndSortedEmployees.map((employee) => (
                  <tr
                    key={employee.id}
                    className="employee-row"
                    data-talent-id={employee.__raw?.talentId ?? employee.id ?? employee.__raw?.ROWID}
                  >
                    <td>{employee.id}</td>
                    <td>{employee.alias || employee.name}</td>
                    <td>{employee.role}</td>
                    <td>
                      <span className="stage-badge" style={{ backgroundColor: getStageColor(employee.stage) }}>
                        {employee.stage}
                      </span>
                    </td>
                    <td>
                      <span className="stage-badge" style={{ backgroundColor: getStageColor(employee.stage) }}>
                        {employee.status ?? '—'}
                      </span>
                    </td>
                    <td className="employee-actions">
                    <div className="action-buttons">
                      <ActionButtons employee={employee} />
                    </div>
                  </td>
                  </tr>
                ))}

                {!err && !loading && !errorMsg && filteredAndSortedEmployees.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>
                      No employees match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {errorMsg && (
            <div style={{ marginTop: 12, color: '#c00', fontSize: 14 }}>
              Save error: {errorMsg}
            </div>
          )}
        </section>

        {/* By Stage sections — show only selected stage when deep-linked; else all */}
        {/* {
          !activeStatStage && (
            <section className="table-section" style={{ marginTop: 32 }}>
              <div className="table-header">
                <h2 className="section-title">By Stage</h2>
                <span className="results-count">{employees.length} total</span>
              </div>

              {(deepLinkedStage ? [deepLinkedStage] : STAGES).map(renderStageSection)}
            </section>
          )
        } */}
      </main>

      {/* Talent modal */}
      <TalentProfileModal isOpen={modalOpen} onClose={closeTalentModal} talent={modalTalent} />

      {/* Gamified cancel confirm */}
      <GamifiedConfirmModal
        isOpen={cancelOpen}
        onClose={() => { setCancelOpen(false); setCancelRow(null); }}
        onConfirm={() => handleCancelShortlist(cancelRow)}
        talentName={cancelRow?.alias || cancelRow?.name || 'this talent'}
        creditDelta={0}
      />

      {/* Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div 
          className="verification-prompt-overlay" 
          onClick={() => setShowVerificationPrompt(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(10px)',
            animation: 'fadeIn 0.4s ease-out'
          }}
        >
          <div 
            className="verification-prompt-modal" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '90%',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              animation: 'slideInUp 0.4s ease-out'
            }}
          >
            <div style={{ marginBottom: '30px', position: 'relative' }}>
              <div style={{ 
                fontSize: '64px', 
                marginBottom: '20px',
                animation: 'bounce 2s infinite'
              }}>
                🔒
              </div>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                margin: '0 0 15px 0',
                textShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Account Verification Required
              </h2>
              <p style={{ 
                color: '#b8c5d6', 
                fontSize: '18px', 
                lineHeight: '1.6',
                margin: '0 0 20px 0'
              }}>
                To start pre-onboarding, you need to complete account verification first!
              </p>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ 
                color: '#ffffff', 
                fontSize: '20px', 
                fontWeight: '600', 
                margin: '0 0 20px 0' 
              }}>
                Verify your account to receive:
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '15px',
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <div style={{ 
                    fontSize: '32px',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px'
                  }}>
                    🎫
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                    <span style={{ color: '#4ade80', fontSize: '24px', fontWeight: '700' }}>10</span>
                    <span style={{ color: '#b8c5d6', fontSize: '16px', fontWeight: '500' }}>Screening Credits</span>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '15px',
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <div style={{ 
                    fontSize: '32px',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px'
                  }}>
                    👥
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                    <span style={{ color: '#4ade80', fontSize: '24px', fontWeight: '700' }}>3</span>
                    <span style={{ color: '#b8c5d6', fontSize: '16px', fontWeight: '500' }}>Talent Role Passes</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowVerificationPrompt(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#b8c5d6',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  minWidth: '150px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#b8c5d6';
                }}
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowVerificationPrompt(false);
                  // Navigate to profile page with verification tab
                  window.location.href = '/profile?tab=profile';
                }}
                style={{
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
                  transition: 'all 0.3s ease',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(74, 222, 128, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(74, 222, 128, 0.3)';
                }}
              >
                Verify Account Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
